/**
 * License editor helpers
 */

export class LicenseContact {

  constructor(data) {
    this.owners = [];
    this.address = '';
    this.email = '';
    this.phone = '';
    this.parse(data);
  }

  get isComplete() {
    return Boolean(this.name) && Boolean(this.address) && Boolean(this.email) && Boolean(this.phone);
  }

  get name() {
    return this.owners.join('\n');
  }

  set name(name) {
    this.owners = name.split(/\n/g);
  }

  get owners() {
    return this._owners.length === 0 ? this.defaultOwners : this._owners;
  }
  set owners(owners) {
    this._owners = owners;
  }

  get address() {
    return this._address || this.defaultAddress;
  }

  set address(address) {
    this._address = address;
  }

  parse(data) {
    data = data || {};
    this._owners = data.owners || [];
    this._address = data.address || '';
    this.email = data.email || '';
    this.phone = data.phone || '';
    this.defaultOwners = [];
    this.defaultAddress = '';
  }

  serialize() {
    return {
      owners: this.owners || [],
      address: this.address || null,
      email: this.email || null,
      phone: this.phone || null
    };
  }
}

export function getCustomFieldValue(customFieldValues, id) {
  const fv = (customFieldValues || []).filter(value => value.id === id)[0];
  const value = fv && fv.value;
  return value === undefined ? null : value;
}

export function setCustomFieldValue(customFieldValues, id, value) {
  let values = (customFieldValues || []).filter(fv => fv.id !== id);
  values.push({ id, value });
  return values;
}

export function computeCustomFieldData(customFieldValues) {
  let data = {};
  (customFieldValues || []).forEach(fv => {
    data[fv.id] = fv.value;
  });
  return data;
}

function customFieldValuesEqual(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * Apply a narrow set of answer changes without dropping untouched, legacy, or
 * no-longer-configured custom field values.
 *
 * @param {Array<{id:string,value:any}>} customFieldValues
 * @param {Array<{id:string,value:any}>} changes
 * @return {{customFieldValues:Array,changedIds:string[]}}
 */
export function mergeCustomFieldValueChanges(customFieldValues, changes) {
  const existingValues = Array.isArray(customFieldValues) ? customFieldValues : [];
  const requestedChanges = Array.isArray(changes) ? changes : [];
  const actualChanges = requestedChanges.filter(change => (
    !customFieldValuesEqual(getCustomFieldValue(existingValues, change.id), change.value)
  ));

  if (actualChanges.length === 0) {
    return {
      customFieldValues: existingValues,
      changedIds: []
    };
  }

  const changedIds = new Set(actualChanges.map(change => change.id));
  const existingById = new Map();
  existingValues.forEach(fieldValue => {
    if (fieldValue?.id && !existingById.has(fieldValue.id)) {
      existingById.set(fieldValue.id, fieldValue);
    }
  });

  return {
    customFieldValues: [
      ...existingValues.filter(fieldValue => !changedIds.has(fieldValue?.id)),
      ...actualChanges.map(change => ({
        ...(existingById.get(change.id) || {}),
        id: change.id,
        value: change.value
      }))
    ],
    changedIds: actualChanges.map(change => change.id)
  };
}

export function buildLicenseEditorIndexKeys({ addressFormatted, id, contactName }) {
  const contact = contactName || '';
  return (addressFormatted + ' ' + id + ' ' + contact)
    .split(/ /g)
    .map(word => word.trim())
    .filter(word => !!word);
}

export function buildLicenseEditorPayload({
  propertyId,
  contact,
  preExistingListingUrls,
  listings,
  customFieldValues,
  appliedOn,
  status,
  paid,
  licenseId,
  issuedOn,
  includeProperty,
  property
}) {
  const contactData = typeof contact?.serialize === 'function' ? contact.serialize() : contact;
  let data = {
    propertyId,
    contact: contactData,
    listings: [...(preExistingListingUrls || []), ...(listings || [])],
    customFieldValues: customFieldValues || [],
    appliedOn,
    status,
    paid,
    licenseId,
    issuedOn
  };
  if (property?.id === propertyId && includeProperty) {
    data.property = property?._data || property;
  }
  return data;
}
